/**
 * Header behaviour: panels, scroll state, mobile drawer, locale popover.
 *
 * Everything here is an enhancement. The panels are plain menus, the drawer
 * contents are in the document, and the localization form submits on its own.
 * If this module fails to load the header still navigates.
 */
import { animate, prefersReducedMotion, trapFocus } from 'theme:motion';

const header = document.querySelector('[data-site-header]');

/** Declared before the scroll handler because that handler reads it. */
let openPanelId = null;

/* ---------------------------------------------------------------------------
   Scroll state

   An IntersectionObserver on a zero-height sentinel rather than a scroll
   listener, so nothing runs on the main thread while the customer scrolls.
   --------------------------------------------------------------------------- */

if (header) {
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:100%;pointer-events:none';
  document.body.prepend(sentinel);

  new IntersectionObserver(
    ([entry]) => header.toggleAttribute('data-solid', !entry.isIntersecting),
    { rootMargin: '-1px 0px 0px 0px' }
  ).observe(sentinel);

  /* -------------------------------------------------------------------------
     Sticky offset

     Anything that sticks below the header has to follow it. Two reasons a
     fixed offset was wrong: the header is taller than --header-height once the
     announcement bar is counted, and it slides away on downward scroll, which
     left the collection filter bar floating with a band of nothing above it.

     Published as a variable rather than wired to one consumer, so the next
     sticky element inherits the behaviour instead of repeating the bug. An
     attribute observer catches every path that toggles data-hidden, including
     the panel and drawer code further down.
     ------------------------------------------------------------------------- */

  const publishStickyTop = () => {
    const resting = header.hasAttribute('data-hidden')
      ? 0
      : Math.round(header.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--sticky-top', `${resting}px`);
  };

  publishStickyTop();
  new MutationObserver(publishStickyTop).observe(header, {
    attributes: true,
    attributeFilter: ['data-hidden'],
  });
  new ResizeObserver(publishStickyTop).observe(header);

  // Hide on the way down, reveal on the way up. Only past a threshold, so the
  // header never flickers during small corrective scrolls.
  let lastY = window.scrollY;
  let ticking = false;

  const onScroll = () => {
    const y = window.scrollY;
    const goingDown = y > lastY;
    const pastThreshold = y > 200;

    if (!openPanelId && !document.querySelector('.nav-drawer.is-open')) {
      header.toggleAttribute('data-hidden', goingDown && pastThreshold);
    }

    lastY = y;
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onScroll);
    },
    { passive: true }
  );
}

/* ---------------------------------------------------------------------------
   Navigation panels
   --------------------------------------------------------------------------- */

const triggers = document.querySelectorAll('[data-nav-trigger]');
const panelHost = document.querySelector('[data-nav-panels]');
let intentTimer = null;

/** Opening on hover needs a short delay, otherwise crossing the bar on the way
 *  somewhere else flashes every panel in turn. */
const HOVER_INTENT_MS = 120;

function closePanel() {
  if (!openPanelId) return;
  const panel = panelHost?.querySelector(`[data-nav-panel="${openPanelId}"]`);
  const trigger = document.querySelector(`[data-nav-trigger="${openPanelId}"]`);
  trigger?.setAttribute('aria-expanded', 'false');
  if (panel) panel.hidden = true;
  openPanelId = null;
}

function openPanel(id) {
  if (openPanelId === id) return;
  closePanel();

  const panel = panelHost?.querySelector(`[data-nav-panel="${id}"]`);
  const trigger = document.querySelector(`[data-nav-trigger="${id}"]`);
  if (!panel || !trigger) return;

  panel.hidden = false;
  const inner = panel.querySelector('.nav-panel__inner');
  const linksOnly = !panel.querySelector('.nav-panel__cards > li');
  panel.toggleAttribute('data-links-only', linksOnly);

  if (linksOnly && inner) {
    const styles = getComputedStyle(inner);
    const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
    const innerBox = inner.getBoundingClientRect();
    const contentLeft = innerBox.left + paddingLeft;
    const contentWidth = inner.clientWidth - paddingLeft - paddingRight;
    const listWidth = Math.min(contentWidth, 288);
    const anchor = Math.max(
      0,
      Math.min(trigger.getBoundingClientRect().left - contentLeft, contentWidth - listWidth)
    );
    panel.style.setProperty('--nav-panel-anchor', `${Math.round(anchor)}px`);
  }

  trigger.setAttribute('aria-expanded', 'true');
  openPanelId = id;
  header?.removeAttribute('data-hidden');

  if (prefersReducedMotion()) return;

  animate(panel, { opacity: [0, 1], transform: ['translateY(-8px)', 'translateY(0px)'] }, {
    duration: 0.32,
    easing: [0.16, 1, 0.3, 1],
  });
  animate(
    panel.querySelectorAll('.nav-panel__card, .nav-panel__list li, .nav-panel__feature'),
    { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0px)'] },
    { duration: 0.4, delay: (index) => 0.04 * index, easing: [0.16, 1, 0.3, 1] }
  );
}

triggers.forEach((trigger) => {
  const id = trigger.dataset.navTrigger;

  trigger.addEventListener('click', () => {
    openPanelId === id ? closePanel() : openPanel(id);
  });

  trigger.addEventListener('pointerenter', () => {
    clearTimeout(intentTimer);
    intentTimer = setTimeout(() => openPanel(id), HOVER_INTENT_MS);
  });

  trigger.addEventListener('pointerleave', () => clearTimeout(intentTimer));
  trigger.addEventListener('focus', () => openPanel(id));
});

// Leaving the header area closes whatever is open; so does Escape, which then
// returns focus to the trigger that opened it.
header?.addEventListener('pointerleave', () => {
  clearTimeout(intentTimer);
  closePanel();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape' && openPanelId) {
    const trigger = document.querySelector(`[data-nav-trigger="${openPanelId}"]`);
    closePanel();
    trigger?.focus();
  }
});

document.addEventListener('focusin', (event) => {
  if (openPanelId && !header?.contains(event.target)) closePanel();
});

/* ---------------------------------------------------------------------------
   Mobile drawer
   --------------------------------------------------------------------------- */

const drawer = document.querySelector('[data-nav-drawer]');
const drawerDialog = drawer?.querySelector('[data-nav-drawer-dialog]');
let releaseDrawerFocus = null;

function openDrawer(trigger) {
  if (!drawer || !drawerDialog) return;
  if (!drawerDialog.open) drawerDialog.showModal();
  drawer.classList.add('is-open');
  document.documentElement.style.overflow = 'hidden';
  trigger?.setAttribute('aria-expanded', 'true');
  releaseDrawerFocus = trapFocus(drawerDialog);
  requestAnimationFrame(() => drawer.querySelector('[data-nav-drawer-close]')?.focus());
}

function closeDrawer() {
  if (!drawer || !drawerDialog) return;
  drawer.classList.remove('is-open');
  document.documentElement.style.overflow = '';
  document.querySelector('[data-nav-drawer-open]')?.setAttribute('aria-expanded', 'false');

  const finish = () => {
    if (drawerDialog.open) drawerDialog.close();
    releaseDrawerFocus?.();
    releaseDrawerFocus = null;
  };

  prefersReducedMotion() ? finish() : setTimeout(finish, 420);
}

document.addEventListener('click', (event) => {
  const open = event.target.closest('[data-nav-drawer-open]');
  if (open) {
    event.preventDefault();
    openDrawer(open);
    return;
  }
  if (event.target.closest('[data-nav-drawer-close]')) {
    event.preventDefault();
    closeDrawer();
  }
});

drawerDialog?.addEventListener('cancel', (event) => {
  event.preventDefault();
  closeDrawer();
});

/* ---------------------------------------------------------------------------
   Country and language modal

   One modal for the whole document, opened from the header, the mobile drawer
   or the footer. Same dialog contract as the cart and the search: it works
   without this module because the trigger is inside a document that already
   holds a real localization form.
   --------------------------------------------------------------------------- */

const localizationModal = document.querySelector('[data-localization-modal]');
const localizationDialog = localizationModal?.querySelector('[data-localization-dialog]');
let releaseLocalizationFocus = null;

function openLocalization(trigger) {
  if (!localizationModal || !localizationDialog) return;
  if (!localizationDialog.open) localizationDialog.showModal();
  localizationModal.classList.add('is-open');
  document.documentElement.style.overflow = 'hidden';
  trigger?.setAttribute('aria-expanded', 'true');
  releaseLocalizationFocus = trapFocus(localizationDialog);
  requestAnimationFrame(() => localizationDialog.querySelector('select')?.focus());
}

function closeLocalization() {
  if (!localizationModal || !localizationDialog) return;
  localizationModal.classList.remove('is-open');
  document.documentElement.style.overflow = '';
  document
    .querySelectorAll('[data-localization-trigger]')
    .forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

  const finish = () => {
    if (localizationDialog.open) localizationDialog.close();
    releaseLocalizationFocus?.();
    releaseLocalizationFocus = null;
  };

  prefersReducedMotion() ? finish() : setTimeout(finish, 420);
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-localization-trigger]');
  if (trigger) {
    event.preventDefault();
    openLocalization(trigger);
    return;
  }
  if (event.target.closest('[data-localization-close]')) {
    event.preventDefault();
    closeLocalization();
  }
});

localizationDialog?.addEventListener('cancel', (event) => {
  event.preventDefault();
  closeLocalization();
});
